import {CommonTooltips} from "@mcleod/common";
import {
    ClickEvent,
    Component,
    DataSourceMode,
    Snackbar,
    TableRow,
    TableRowDisplayEvent,
    Textbox,
    Toast, Button
} from "@mcleod/components";
import { ImageName } from "@mcleod/images";
import {Api, ModelRow, StringUtil, LogManager} from "@mcleod/core";
import {CashAdvanceLayoutProps, CashAdvanceSlideout} from "./CashAdvanceSlideout";
import {FuelCardDisplayUtils} from "./FuelCardDisplayUtils";
import { AutogenLayoutDriverFuelCard } from "./autogen/AutogenLayoutDriverFuelCard";
import {FuelInterface} from "@mcleod/integrations";



enum FuelStatus {
    ACTIVE = "A",
    INACTIVE = "I",
    HOLD = "H",
    BLOCKED = "B",
    DELETED = "D",
    FRAUD = "F",
    CLONED = "C",
    LOST = "L",
    STOLEN = "S",
    PERMANENTLY_BLOCKED = "X"
}

const log = LogManager.getLogger("lme.dispatch.asset.DriverFuelCard");


interface ChangeCardStatusRequest {
    driver_id: string;
    payee_id: string;
    tractor_id: string;
    fuel_interface_id: FuelInterface;
    card_status: string;
    card_number: string;
    account_id: string;
    sub_account_id: string;
}

export class DriverFuelCard extends AutogenLayoutDriverFuelCard {

    private _driverFullName: string;
    private _driverId: string;
    private readonly _invalidIFuelUserMessage = "No valid IFuel User found. Card status cannot be changed";

    get driverFullName(): string {
        return this._driverFullName;
    }

    set driverFullName(value: string) {
        this._driverFullName = value;
    }

    get driverId(): string {
        return this._driverId;
    }

    set driverId(value: string) {
        this._driverId = value;
    }

    /** This is an event handler for the onRowDisplay event of tableFuelCards. */
    tableFuelCardsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.target as TableRow;
        const data = tableRow.data as ModelRow;
        const textboxPayee = tableRow.findComponentById("textboxPayeeId") as Textbox;
        const fuelCardImage = tableRow.findComponentById("buttonFuelCardAction") as Button;
        const textboxTractor = tableRow.findComponentById("textboxTractorId") as Textbox;

        const textboxCardStatus = tableRow.findComponentById("textboxCardStatus") as Textbox;
        const fuelInterfaceId: string = data.get("fuel_interface_id");
        const cardStatus: string = data.get("card_status");
        textboxCardStatus.text = this.cardStatusDescription(fuelInterfaceId, cardStatus);

        const buttonCashAdvanceLimit = tableRow.findComponentById("buttonCashAdvanceLimit");

        const showFuelCardRow: boolean = data?.get("show_fuel_card", false);
        const showImageIcons: boolean = data?.get("show_image_icon", false);
        const userHasIFuelUserRecord: boolean = data?.get("has_valid_ifuel_user", false);

        fuelCardImage.visible = showImageIcons;
        const imageName = this.imageIconName(cardStatus, fuelInterfaceId);
        fuelCardImage.imageName = imageName;

        let isFuelCardImageInteractive = false;
        let fuelCardImageTooltip = null;

        if (showImageIcons) {
            isFuelCardImageInteractive = !StringUtil.isEmptyString(imageName) && userHasIFuelUserRecord;
            if (isFuelCardImageInteractive) {
                fuelCardImageTooltip = this.createIconTooltip(cardStatus, fuelInterfaceId);
            }
        }

        fuelCardImage.tooltip = fuelCardImageTooltip;

        if (userHasIFuelUserRecord === false) {
            fuelCardImage.tooltip = this._invalidIFuelUserMessage;
        }

        let hasValidFuelUserRecords: boolean;
        if ("H" === fuelInterfaceId) {
            hasValidFuelUserRecords = data?.get("has_valid_efs_user", false);
        } else {
            hasValidFuelUserRecords = data?.get("has_valid_comdata_user", false);
        }

        const shouldShowAndEnableCashAdvanceIcon = this.shouldDisplayCashAdvanceIcon(cardStatus, hasValidFuelUserRecords) && showImageIcons;
        buttonCashAdvanceLimit.visible = shouldShowAndEnableCashAdvanceIcon;
        buttonCashAdvanceLimit.enabled = shouldShowAndEnableCashAdvanceIcon;

        const accountId: string = data.get("account_id");
        const subaccountId: string = data.get("sub_account");
        const fuelCardNumber: string = data.get("id");

        const driverId: string = data.get("driver_id");
        const tractorId: string = data.get("tractor_id");
        const payeeId: string = data.get("payee_id");

        fuelCardImage.addClickListener((event: ClickEvent) => {
            if (userHasIFuelUserRecord === true) {
                this.activateOrBlockFuelCard(driverId, payeeId, tractorId, fuelInterfaceId, cardStatus, fuelCardNumber, accountId, subaccountId);
            } else {
                this.showInvalidIFuelUserWarning();
            }
        });
        CommonTooltips.setTooltipContentLayout(textboxPayee, payeeId, "lme/dispatch/PayeeQuickInfo", {width: 220});
        CommonTooltips.setTooltipContentLayout(textboxTractor, tractorId, "lme/dispatch/asset/TractorQuickInfo");
        tableRow.visible = showFuelCardRow;
    }

    private showInvalidIFuelUserWarning(): void {
        Snackbar.showWarningSnackbar(this._invalidIFuelUserMessage);
    }

    async activateOrBlockFuelCard(
        driverId: string, payeeId:
        string, tractorId: string,
        fuelInterfaceId: string,
        cardStatus: string,
        fuelCardNumber: string,
        accountId: string,
        subaccountId: string) {

        const changeRequest: ChangeCardStatusRequest = {
            driver_id: driverId,
            payee_id: payeeId,
            tractor_id: tractorId,
            fuel_interface_id: fuelInterfaceId as FuelInterface,
            card_status: cardStatus,
            card_number: fuelCardNumber,
            account_id: accountId,
            sub_account_id: subaccountId
        };

        if (FuelInterface.EFS === fuelInterfaceId) {
            await this.handleCardStatusChange(changeRequest, "lme/dispatch/asset/fuel-card-activate-block");
        }
        if (FuelInterface.COMDATA === fuelInterfaceId) {
            await this.handleCardStatusChange(changeRequest, "lme/dispatch/asset/fuel-card-purchase-on-off");
        }
    }

    private async handleCardStatusChange(
        changeCardStatusRequest: ChangeCardStatusRequest,
        endpoint: string) {

        try {
            const response = await Api.search(endpoint, changeCardStatusRequest);
            if (response) {
                const message = response.data[0].response;
                const actionComplete: boolean = response.data[0].action_complete;
                if (actionComplete) {
                    Toast.showToast(message);
                    await this.mainDataSource.search();
                } else {
                    Snackbar.showWarningSnackbar(message);
                    await this.mainDataSource.search();
                }
            }
        } catch (error) {
            log.error(error);
            Snackbar.showWarningSnackbar(error.messages[0]);
        }
    }

    cardStatusDescription(interfaceId: string, cardStatus: string): string {
        if (cardStatus === FuelStatus.ACTIVE) return "Active";
        if (cardStatus === FuelStatus.DELETED) return "Deleted";
        if (cardStatus === FuelStatus.HOLD) return "Hold";
        if (cardStatus === FuelStatus.FRAUD) return "Fraud";
        if (cardStatus === FuelStatus.CLONED) return "Cloned";
        if (cardStatus === FuelStatus.LOST) return "Lost";
        if (cardStatus === FuelStatus.PERMANENTLY_BLOCKED) return "Permanently blocked";
        if (cardStatus === FuelStatus.STOLEN) return "Stolen";
        if (cardStatus === FuelStatus.INACTIVE) return FuelInterface.COMDATA === interfaceId ? "No company purchase" : "Inactive";
        return "Unknown";
    }

    imageIconName(cardStatus: string, interfaceId: string): ImageName {
        if (FuelInterface.COMDATA !== interfaceId && FuelInterface.EFS !== interfaceId) {
            return null;
        }
        let imageName: ImageName = null;
        if (FuelStatus.ACTIVE === cardStatus) {
            imageName = ImageName.fuelCardActivate;
        }
        if (FuelStatus.HOLD === cardStatus || FuelStatus.BLOCKED === cardStatus || FuelStatus.INACTIVE === cardStatus) {
            imageName = ImageName.fuelCardBlock;
        }
        return imageName;
    }

    private shouldDisplayCashAdvanceIcon(cardStatus: string, hasValidFuelUserRecord: boolean): boolean {
        return FuelCardDisplayUtils.shouldDisplayDriverCashAdvanceIcon(cardStatus, hasValidFuelUserRecord)
            && this.mainDataSource.mode !== DataSourceMode.SEARCH;
    }

    newCardStatus(cardStatus: string, interfaceId: string): string {
        if (FuelStatus.ACTIVE === cardStatus && FuelInterface.COMDATA === interfaceId) return "No company purchase";
        if (FuelStatus.ACTIVE === cardStatus) return "Hold";
        if (FuelStatus.INACTIVE === cardStatus || FuelStatus.HOLD === cardStatus) return "Active";
        return "Unknown";
    }

    createIconTooltip(cardStatus: string, interfaceId: string): string {
        const messages = {
            [FuelInterface.COMDATA]: {
                [FuelStatus.ACTIVE]: "Current status: Active\n\nSet status to 'No company purchase'",
                [FuelStatus.HOLD]: "Current status: No company purchase\n\nSet status to 'Active'",
                [FuelStatus.INACTIVE]: "Current status: No company purchase\n\nSet status to 'Active'"
            },
            [FuelInterface.EFS]: {
                [FuelStatus.ACTIVE]: "Current status: Active\n\nSet status to 'Hold' and remove overrides",
                [FuelStatus.HOLD]: "Current status: Hold\n\nSet status to 'Active'",
                [FuelStatus.INACTIVE]: "Current status: Inactive\n\nSet status to 'Active'"
            }
        };
        return messages[interfaceId]?.[cardStatus];
    }

    async showCashAdvanceSlideout(event: ClickEvent): Promise<void> {
        const advanceImage = event.target as Component;
        const tableRow = TableRow.getContainingTableRow(advanceImage);
        const data = tableRow.data;

        if (!data) {
            log.error("No data found for CashAdvanceSlideout");
            return;
        }

        const props: CashAdvanceLayoutProps = {
            driverFullName: this.driverFullName,
            cardNum: data.get("id"),
            vendorInterfaceId: data.get("fuel_interface_id"),
            vendorDesc: data.get("fuel_interface.descr"),
            accountId: data.get("account_id"),
            subAccountId: data.get("sub_account"),
            driverId: this.driverId,
            payeeId: data.get("payee_id"),
            tractorId: data.get("tractor_id"),
            assigneeType: data.get("assignee_type"),
            cardStatus: data.get("card_status"),
        }
        await CashAdvanceSlideout.showSlideout(props);
    }

}
